import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  query, 
  where, 
  Timestamp 
} from "firebase/firestore";
import { db } from "../../_db";
import { DistributionData, DistributionPayload, DistributionUpdatePayload } from "@/types/distribution/distribution.types";
import { distributionCalculationService, DistributionCalculationInputs } from "./distributionCalculationService";
import { TransportDistribution } from "@/types/transport-distribution/transport-distribution.types";
import { FootprintProduct } from "@/types/results";

const COLLECTION_NAME = "distribution_data";

export const distributionService = {
  // Create new distribution data with calculations
  async createDistribution(
    payload: DistributionPayload, 
    userId: string, 
    companyId: string,
    transportDistributions: TransportDistribution[],
    product: FootprintProduct,
    companyType: string = "transformation"
  ): Promise<string> {
    try {
      // Calculate footprints
      const calculationInputs: DistributionCalculationInputs = {
        transportData: payload.transport_data,
        transportDistributions,
        product,
        companyType,
        footprintType: payload.footprint_type as 'carbon' | 'hydric' | 'both'
      };

      const calculationResult = distributionCalculationService.calculateDistributionFootprints(calculationInputs);

      // Update the product with distribution calculations
      await distributionCalculationService.updateFootprintProductWithDistribution(
        payload.product_id,
        calculationResult,
        payload.footprint_type as 'carbon' | 'hydric' | 'both'
      );

      // Create distribution data record
      const docRef = await addDoc(collection(db, COLLECTION_NAME), {
        ...payload,
        total_carbon_footprint: calculationResult.carbon_footprint.total_kgCO2e,
        total_hydric_footprint: calculationResult.hydric_footprint.total_m3,
        company_id: companyId,
        created_at: Timestamp.now(),
        updated_at: Timestamp.now(),
        created_by: userId,
      });
      return docRef.id;
    } catch (error) {
      console.error("Error creating distribution data:", error);
      throw new Error("Failed to create distribution data");
    }
  },

  // Update existing distribution data with calculations
  async updateDistribution(
    distributionId: string, 
    payload: DistributionUpdatePayload, 
    userId: string,
    transportDistributions: TransportDistribution[],
    product: FootprintProduct,
    companyType: string = "transformation",
    footprintType: 'carbon' | 'hydric' | 'both' = 'both'
  ): Promise<void> {
    try {
      // Calculate footprints
      const calculationInputs: DistributionCalculationInputs = {
        transportData: payload.transport_data,
        transportDistributions,
        product,
        companyType,
        footprintType
      };

      const calculationResult = distributionCalculationService.calculateDistributionFootprints(calculationInputs);

      // Update the product with distribution calculations
      await distributionCalculationService.updateFootprintProductWithDistribution(
        product.id,
        calculationResult,
        footprintType
      );

      // Update distribution data record
      const docRef = doc(db, COLLECTION_NAME, distributionId);
      await updateDoc(docRef, {
        ...payload,
        total_carbon_footprint: calculationResult.carbon_footprint.total_kgCO2e,
        total_hydric_footprint: calculationResult.hydric_footprint.total_m3,
        updated_at: Timestamp.now(),
        updated_by: userId,
      });
    } catch (error) {
      console.error("Error updating distribution data:", error);
      throw new Error("Failed to update distribution data");
    }
  },

  // Delete distribution data
  async deleteDistribution(distributionId: string, productId?: string): Promise<void> {
    try {
      const docRef = doc(db, COLLECTION_NAME, distributionId);
      await deleteDoc(docRef);
      
      if (productId) {
        const productRef = doc(db, "footprint_products", productId);
        await updateDoc(productRef, {
          distribution: null,
          updated_at: new Date()
        });
      }
    } catch (error) {
      console.error("Error deleting distribution data:", error);
      throw new Error("Failed to delete distribution data");
    }
  },

  // Get distribution data by company
  async getDistributionByCompany(companyId: string): Promise<DistributionData[]> {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where("company_id", "==", companyId)
      );
      
      const querySnapshot = await getDocs(q);
      const distributions: DistributionData[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        distributions.push({
          id: doc.id,
          ...data,
          created_at: data.created_at?.toDate() || new Date(),
          updated_at: data.updated_at?.toDate() || new Date(),
        } as DistributionData);
      });
      
      // Sort by created_at in descending order on the client side
      return distributions.sort((a, b) => b.created_at.getTime() - a.created_at.getTime());
    } catch (error) {
      console.error("Error fetching distribution data:", error);
      throw new Error("Failed to fetch distribution data");
    }
  },

  // Get distribution data by product
  async getDistributionByProduct(companyId: string, productId: string): Promise<DistributionData[]> {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where("company_id", "==", companyId),
        where("product_id", "==", productId)
      );
      
      const querySnapshot = await getDocs(q);
      const distributions: DistributionData[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        distributions.push({
          id: doc.id,
          ...data,
          created_at: data.created_at?.toDate() || new Date(),
          updated_at: data.updated_at?.toDate() || new Date(),
        } as DistributionData);
      });
      
      // Sort by created_at in descending order on the client side
      return distributions.sort((a, b) => b.created_at.getTime() - a.created_at.getTime());
    } catch (error) {
      console.error("Error fetching distribution data by product:", error);
      throw new Error("Failed to fetch distribution data by product");
    }
  },

  // Get distribution data by footprint type
  async getDistributionByFootprintType(
    companyId: string, 
    footprintType: 'carbon' | 'hydric'
  ): Promise<DistributionData[]> {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where("company_id", "==", companyId),
        where("footprint_type", "==", footprintType)
      );
      
      const querySnapshot = await getDocs(q);
      const distributions: DistributionData[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        distributions.push({
          id: doc.id,
          ...data,
          created_at: data.created_at?.toDate() || new Date(),
          updated_at: data.updated_at?.toDate() || new Date(),
        } as DistributionData);
      });
      
      // Sort by created_at in descending order on the client side
      return distributions.sort((a, b) => b.created_at.getTime() - a.created_at.getTime());
    } catch (error) {
      console.error("Error fetching distribution data by footprint type:", error);
      throw new Error("Failed to fetch distribution data by footprint type");
    }
  },

  // Delete all distribution data for a specific product
  async deleteDistributionByProduct(productId: string): Promise<void> {
    try {
      // Find all distribution data for this product
      const q = query(
        collection(db, COLLECTION_NAME),
        where("product_id", "==", productId)
      );
      
      const querySnapshot = await getDocs(q);
      
      // Delete all distribution data documents for this product
      const deletePromises = querySnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      
      console.log(`Deleted ${querySnapshot.docs.length} distribution data documents for product ${productId}`);
    } catch (error) {
      console.error("Error deleting distribution data by product:", error);
      throw new Error("Failed to delete distribution data for product");
    }
  }
};
